import { useMemo } from 'react';
import { doesEventFieldMatchSearch } from './doesEventFieldMatchSearch';
import { addPredictiveGoalsIcon } from '../../addPredictiveGoalsIcon';
import { MappingMetadata } from '../../../hooks/queries/mappings/types';
import { useTranslation } from '../../../hooks/useTranslation';
import { useGetFieldDisplayName } from '../../../hooks/useGetFieldDisplayName';
import { ClientDataType } from '../../../types';
import { CAMPAIGN_FIELD, CAMPAIGN_LABEL_CATEGORY } from '../constants';

type UseFieldOptionsProps = {
  search: string;
  mappings: MappingMetadata[];
  dataType: ClientDataType;
};

type EventFieldOption = {
  title: string;
  value: string;
  badge: string;
};

export const useEventFieldOptions = ({ search, mappings, dataType }: UseFieldOptionsProps) => {
  const { t } = useTranslation();
  const { getFieldDisplayName } = useGetFieldDisplayName();

  return useMemo(
    () =>
      mappings
        .reduce((acc, { name, fieldType }) => {
          const fieldDisplayName = getFieldDisplayName(name, dataType);

          // Special case to handle campaign label id
          if (name === CAMPAIGN_FIELD) {
            const campaignLabelFieldDisplayName = getFieldDisplayName(
              CAMPAIGN_LABEL_CATEGORY.field,
              dataType,
            );
            const newAcc = [...acc];
            if (doesEventFieldMatchSearch(fieldDisplayName, fieldType, search)) {
              newAcc.push({
                title: fieldDisplayName,
                badge: t(`fieldTypes.${fieldType}`),
                value: name,
              });
            }
            if (doesEventFieldMatchSearch(campaignLabelFieldDisplayName, fieldType, search)) {
              newAcc.push({
                title: campaignLabelFieldDisplayName,
                badge: t(`fieldTypes.${CAMPAIGN_LABEL_CATEGORY.fieldType}`),
                value: CAMPAIGN_LABEL_CATEGORY.field,
              });
            }
            return newAcc;
          }

          // General case
          return doesEventFieldMatchSearch(fieldDisplayName, fieldType, search)
            ? [
                ...acc,
                {
                  title: getFieldDisplayName(name, dataType),
                  badge: t(`fieldTypes.${fieldType}`),
                  value: name,
                  ...addPredictiveGoalsIcon(name),
                },
              ]
            : acc;
        }, [] as EventFieldOption[])
        .sort(({ title: a }, { title: b }) => a.localeCompare(b)),
    [mappings, search, t, getFieldDisplayName, dataType],
  );
};
